/**
 * Utility functions for sorting operations
 */

import { SortState } from '../components/common/GenericTable';
import { isSet } from './entityTypeDetection';

/**
 * Converts SortState to SCIM API sortBy string format
 * SCIM v2 format: "fieldName:direction" (e.g., "DisplayName:ascending" or "DisplayName:descending")
 * 
 * @param sort - The sort state (column and direction)
 * @param entityType - Optional entity type for special handling (e.g., Set entities)
 * @param attributeMap - Optional custom attribute mapping for column keys to SCIM attributes
 * @returns The sortBy string in SCIM format, or undefined if no sort
 * 
 * @example
 * getSortByString({ column: 'DisplayName', direction: 'asc' })
 * // Returns: "DisplayName:ascending"
 * 
 * @example
 * getSortByString({ column: 'type', direction: 'desc' }, 'Set', { 'type': 'Type' })
 * // Returns: "Type:descending"
 */
export function getSortByString(
    sort: SortState | null,
    entityType?: string,
    attributeMap?: Record<string, string>
): string | undefined {
    if (!sort) return undefined;

    const direction = sort.direction === 'asc' ? 'ascending' : 'descending';

    // Use custom attribute map if provided
    if (attributeMap) {
        const scimAttribute = attributeMap[sort.column] || sort.column;
        return `${scimAttribute}:${direction}`;
    }

    // Default attribute mapping with special handling for Set and Group entities
    const isSetEntity = entityType ? isSet(entityType) : false;
    const isGroupEntity = entityType ? entityType.toLowerCase().startsWith('group') : false;
    const isClassEntity = entityType ? entityType.toLowerCase() === 'class' : false;

    const defaultAttributeMap: Record<string, string> = {
        'displayname': 'DisplayName',
        'name': 'DisplayName',
        'address': 'Address',
        // Classes use Course for the table's "Type/Course" column; Sets/Groups use Type; others use EntityType.
        'type': isClassEntity ? 'Course' : (isSetEntity || isGroupEntity ? 'Type' : 'EntityType'),
        'entitytype': 'EntityType',
        'description': 'Description',
        'id': 'Id',
        ...(isGroupEntity ? {
            'email': 'Email',
            'legacyfilter': 'LegacyFilter',
            'filterscope': 'FilterScope',
            'filter': 'Filter'
        } : {})
    };

    // Try to find in map first (case-insensitive), otherwise capitalize first letter (fallback)
    const scimAttribute = defaultAttributeMap[sort.column.toLowerCase()] ||
        (sort.column.charAt(0).toUpperCase() + sort.column.slice(1).toLowerCase());

    return `${scimAttribute}:${direction}`;
}
